/**
 * Sliding window rate limiter for controlling request frequency.
 * Implements a time-based sliding window algorithm that tracks requests
 * within a configured time period and enforces maximum request limits.
 *
 * Uses a sliding window approach where old requests outside the window
 * are automatically discarded, providing smooth rate limiting without
 * the burst behavior of fixed windows.
 */
export default class RateLimiter {
  /**
   * Creates a new RateLimiter instance with configurable limits.
   *
   * @param {Object} [config={}] - Rate limiter configuration
   * @param {number} [config.windowMs=60000] - Time window in milliseconds (default: 1 minute)
   * @param {number} [config.maxRequests=100] - Maximum requests allowed in window
   */
  constructor(config) {
    this.config = {
      windowMs: 60_000,
      maxRequests: 100,
      ...config,
    };

    this.requests = [];
  }

  /**
   * Checks if a new request is allowed under current rate limits.
   * Automatically cleans up expired request timestamps and adds current request if allowed.
   * @returns {boolean} True if request is within limits, false if rate limit exceeded
   */
  isRequestAllowed() {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    this.requests = this.requests.filter((timestamp) => timestamp > windowStart);
    if (this.requests.length >= this.config.maxRequests) return false;
    this.requests.push(now);

    return true;
  }

  /**
   * Returns current rate limiting statistics for monitoring and debugging.
   * @returns {Object} Rate limiter statistics
   */
  getStats() {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;
    const activeRequests = this.requests.filter((timestamp) => timestamp > windowStart);

    return {
      currentRequests: activeRequests.length,
      maxRequests: this.config.maxRequests,
      windowMs: this.config.windowMs,
      remaining: Math.max(0, this.config.maxRequests - activeRequests.length),
    };
  }

  reset() {
    this.requests = [];
  }

  /**
   * Manually removes expired requests from tracking array.
   * Called automatically by `isRequestAllowed()`, but can be used
   * for periodic maintenance to prevent memory buildup.
   */
  cleanup() {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;
    this.requests = this.requests.filter((timestamp) => timestamp > windowStart);
  }
}
